/* eslint no-return-assign: 0, no-use-before-define: 0 */

import React from 'react';
import { NamespaceConsumer, withNamespace } from '@ux/namespace-component';

let IFrame, Panel;

/**
 * Inapp help dialog component.
 *
 * @class Dialog
 * @public
 */
export class Dialog extends NamespaceConsumer {
  constructor() {
    super(...arguments);

    // Initial state
    this.state.dragging = false;
    this.state.dragX = 0;
    this.state.dragY = 0;

    // Bind event handlers so they have the correct `this` context
    [
      'update',
      'onMouseUp',
      'onMouseDown',
      'onMouseMove',
      'onTouchStart',
      'onTouchEnd',
      'onTouchMove'
    ].forEach((fn) => {
      this[fn] = this[fn].bind(this);
    });
  }

  /**
   * Update the position and height of the dialog when it is first mounted.
   * Set up a window resize listener so that the dialog can be updated as needed
   * on page resize.
   *
   * @private
   */
  componentDidMount() {
    this.update();
    this.on('resize', this.update);
  }

  /**
   * Update the position and height of the dialog when it is updated.
   *
   * @param {object} prevProps The previous props
   * @private
   */
  componentDidUpdate(prevProps) {
    if (this.props.url !== prevProps.url) {
      this.update();
    }
  }

  componentWillUnmount() {
    this.off('resize', this.update);
  }

  /**
   * Update the position and height of the dialog as needed.
   *
   * @public
   */
  update() {
    this.setPosition();
    this.setHeight();
  }

  /**
   * Position the dialog.
   * If no x or y parameters are specified, they are calculated automatically.
   *
   * @param {Number} x - x position (optional)
   * @param {Number} y - y position (optional)
   * @public
   */
  setPosition(x, y) {
    if (!this.dom) return;

    const { rightMargin, minLeft, minTop } = this.props;

    if (typeof x !== 'number') {
      x = window.innerWidth - this.dom.offsetWidth - rightMargin;
      if (x < minLeft) {
        x = minLeft;
      }
    }

    if (typeof y !== 'number') {
      y = minTop;
    }

    this.dom.style.left = x + 'px';
    this.dom.style.top = y + 'px';
  }

  /**
   * Return the current position of the dialog.
   *
   * @returns {Object} x,y position of dialog
   * @public
   */
  getPosition() {
    if (!this.dom) return { };

    const x = parseInt(this.dom.style.left, 10);
    const y = parseInt(this.dom.style.top, 10);
    return { x, y };
  }

  /**
   * Position the dialog.
   *
   * If no height parameter is specified, it is calculated automatically based
   * on the current window size.
   *
   * @param {Number} height - height (optional)
   * @public
   */
  setHeight(height) {
    if (!this.dom) return;

    const { bottomMargin, minHeight } = this.props;
    const currentHeight = this.dom.offsetHeight;
    let heightDelta = 0;

    if (typeof height !== 'number') {
      height = window.innerHeight -
        parseInt(this.dom.style.top, 10) -
        bottomMargin;
    }

    if (height < minHeight) {
      height = minHeight;
    }

    heightDelta = height - currentHeight;

    this.frame.updateHeight(heightDelta);
    this.panel.updateHeight(heightDelta);
    this.dom.style.height = (currentHeight + heightDelta) + 'px';
  }

  /**
   * Get the height of the dialog.
   *
   * @returns {Number} height of the dialog
   * @public
   */
  getHeight() {
    if (!this.dom) return 0;
    return parseInt(this.dom.style.height, 10);
  }

  /**
   * Render the dialog.
   *
   * @returns {ReactElement} the dialog box
   * @public
   */
  render() {
    const { url, onClose } = this.props;

    const titleBar = renderTitleBar(
      <div className={ this.namespace('uxicon', 'uxicon-grab-vert') } />,
      <Close
        onClick={ onClose }
        onTouchStart={ onClose }
        onMouseDown={ event => event.stopPropagation() }
        className={ this.namespace('uxicon', 'uxicon-x') }
      />,
      this.namespace('ux-help-title-bar')
    );

    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    return <div
      role='dialog'
      className={ this.namespace('ux-help-container') }
      onMouseDown={ this.onMouseDown }
      onTouchStart={ this.onTouchStart }
      ref={ (node) => this.dom = node }>
      <Panel dragging={ this.state.dragging } ref={ (node) => this.panel = node }>
        { titleBar }
        <IFrame src={ url } ref={ (node) => this.frame = node } />
      </Panel>
    </div>;
  }


  //
  // Drag and drop moving
  //

  startDrag(x, y) {
    // Add event handlers
    this.on('mouseup', this.onMouseUp);
    this.on('mousemove', this.onMouseMove);
    this.on('touchend', this.onTouchEnd);
    this.on('touchmove', this.onTouchMove);

    // Update state
    this.setState({
      dragging: true,
      dragX: x,
      dragY: y
    });
  }

  stopDrag() {
    if (!this.state.dragging) return;

    // Remove event listeners
    this.off('mouseup', this.onMouseUp);
    this.off('mousemove', this.onMouseMove);
    this.off('touchend', this.onTouchEnd);
    this.off('touchmove', this.onTouchMove);

    // Update state
    this.setState({
      dragging: false,
      dragX: 0,
      dragY: 0
    });
  }

  move(x, y) {
    this.setPosition(x, y);
  }

  keepInBounds(x, y) {
    const { minTop } = this.props;

    const maxX = window.innerWidth - this.dom.offsetWidth;
    const maxY = window.innerHeight - minTop;

    if (x < 0) {
      x = 0;
    } else if (x > maxX) {
      x = maxX;
    }

    if (y < 0) {
      y = 0;
    } else if (y > maxY) {
      y = maxY;
    }

    this.move(x, y);
  }

  onMouseDown(e) {
    if (this.frame && this.frame.dom) this.frame.dom.style.pointerEvents = 'none';

    const downX = e.pageX - e.currentTarget.offsetLeft;
    const downY = e.pageY - e.currentTarget.offsetTop;

    this.startDrag(downX, downY);
    return false;
  }

  onMouseUp(e) {
    if (this.frame && this.frame.dom) this.frame.dom.style.pointerEvents = 'auto';

    const upX = e.pageX - this.state.dragX;
    const upY = e.pageY - this.state.dragY;
    this.keepInBounds(upX, upY);

    this.stopDrag();
    return false;
  }

  onMouseMove(e) {
    const { dragging, dragX, dragY } = this.state;

    if (dragging) {
      this.move(e.pageX - dragX, e.pageY - dragY);
      return false;
    }
  }

  onTouchStart(e) {
    const evt = e.originalEvent || e;
    const downX = evt.touches[0].pageX - e.currentTarget.offsetLeft;
    const downY = evt.touches[0].pageY - e.currentTarget.offsetTop;

    this.startDrag(downX, downY);
    return false;
  }

  onTouchEnd() {
    this.stopDrag();
    return false;
  }

  onTouchMove(e) {
    const evt = e.originalEvent || e;
    const { dragging, dragX, dragY } = this.state;

    if (dragging) {
      this.move(
        evt.touches[0].pageX - dragX,
        evt.touches[0].pageY - dragY
      );

      return false;
    }
  }
}

/**
 * Default properties for the component.
 *
 * @type {Object}
 * @public
 */
Dialog.defaultProps = {
  minLeft: 100,
  minTop: 50,
  rightMargin: 0,
  bottomMargin: 50,
  minHeight: 350
};

/**
 * Inapp help dialog panel component.
 *
 * @private
 */
class PanelRaw extends NamespaceConsumer {
  render() {
    const { dragging, children } = this.props;
    const classNames =  this.namespace(this.classNames('ux-help-panel', {
      'ux-help-positioned': !dragging,
      'ux-help-dragging': dragging
    }));

    return <div className={ classNames } ref={ (node) => this.dom = node }>
      { children }
    </div>;
  }

  /**
   * Update the height of the component based on a delta.
   *
   * @param {Number} delta - The amount of difference to change height by.
   * @private
   */
  updateHeight(delta) {
    if (!this.dom) return;

    const currentHeight = this.dom.offsetHeight;
    this.dom.style.height = (currentHeight + delta) + 'px';
  }
}

Panel = withNamespace(PanelRaw);

/**
 * Generate the title bar
 *
 * @param {Element} grabIcon - div containing the grab icon
 * @param {ReactElement} closeButton - component containing the `x` to close the inapp-help
 * @param {String} classnames - string of classnames
 *
 * @returns {Element} div containing the content of the title bar
 * @private
 */
const renderTitleBar = function (grabIcon, closeButton, classnames) {
  return (<div className={ classnames }>
    { grabIcon }
    { closeButton }
  </div>);
};

/**
 * Inapp help dialog close button component.
 *
 * @param {Object} props - properties of the close button
 *
 * @returns {Element} div containing the close button
 * @private
 */
const Close = (props) => {
  return (<div { ...props } />);
};

/**
 * Inapp help dialog iframe component.
 *
 * @private
 */
class IFrameRaw extends NamespaceConsumer {
  render() {
    // eslint-disable-next-line no-unused-vars
    const { namespace, ...props } = this.props;
    // eslint-disable-next-line jsx-a11y/iframe-has-title
    return (<iframe
      className={ this.namespace('ux-help-content') }
      frameBorder='0' { ...props }
      ref={ (node) => this.dom = node } />);
  }

  /**
   * Update the height of the component based on a delta.
   *
   * @param {Number} delta - The amount of difference to change height by.
   * @private
   */
  updateHeight(delta) {
    if (!this.dom) return;

    const currentHeight = this.dom.offsetHeight;
    this.dom.style.height = (currentHeight + delta) + 'px';
  }
}

IFrame = withNamespace(IFrameRaw);

//
// Export the other components as well
//
Dialog.Panel = Panel;
Dialog.Close = Close;
Dialog.IFrame = IFrame;

export default withNamespace(Dialog);
